import { FC, CSSProperties, useCallback } from 'react'
import cn from 'classnames'
import { useTable, useExpanded } from 'react-table'
import { useMediaQuery } from '@react-hook/media-query'

import { Loading } from 'components/Loading'
import ResultsPerPage from 'components/ResultsPerPage'
import Pagination from './Pagination'
import { ReactComponent as Caret } from 'images/icons/caret-down.svg'

import styles from './Table.module.scss'

const EXPANDER_ID = 'expander'

export const getExpanderCell = () => ({
  id: EXPANDER_ID,
  Cell: ({ row }: any) => (
    <button {...row.getToggleRowExpandedProps()} type="button" className={styles.caretButton}>
      <Caret className={cn(row.isExpanded && styles.caretUp)} />
      Expand
    </button>
  ),
})

interface TableInnerProps {
  columns: any
  results: any
  isFetching: boolean
  isError: boolean
  isSmallAndUp: boolean
  renderRowSubComponent?: any
  className?: string
}

interface TableProps {
  columns: any
  query?: any
  meta?: any
  setQueryParams?: any
  renderRowSubComponent?: any
  className?: string
  resultsPerPage?: number[]
  defaultResultsPerPage?: number
}

const TableInner: FC<TableInnerProps> = ({
  columns,
  results,
  renderRowSubComponent,
  isFetching,
  isError,
  className,
  isSmallAndUp,
}) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, visibleColumns } = useTable(
    {
      columns,
      data: results,
      initialState: {
        ...(!isSmallAndUp && { hiddenColumns: [EXPANDER_ID] }),
      },
    },
    useExpanded,
  )

  // This CSS custom prop is needed to create the dynamic column table
  const columnsCustomProp = { '--columns': columns.length } as CSSProperties

  // Render the UI for your table
  return (
    <>
      <table {...getTableProps()} className={cn(styles.table, className)} style={columnsCustomProp}>
        <thead className={styles.thead}>
          {headerGroups.map((headerGroup) => (
            // The key prop is retrieved by getHeaderGroupProps()
            // eslint-disable-next-line react/jsx-key
            <tr {...headerGroup.getHeaderGroupProps()} className={styles.tr}>
              {headerGroup.headers.map((column) => (
                // The key prop is retrieved by getHeaderProps()
                // eslint-disable-next-line react/jsx-key
                <th {...column.getHeaderProps()} className={styles.th}>
                  {column.render('Header')}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()} className={styles.tbody}>
          {/* No Results or Error message */}
          {!isFetching && (!rows.length || isError) && (
            <tr className={styles.tr}>
              <td
                colSpan={visibleColumns.length}
                className={cn(styles.td, { [styles.noResults]: !rows.length }, { [styles.error]: isError })}
              >
                {isError ? 'There was an error retrieving the data' : 'No results found'}
              </td>
            </tr>
          )}

          {rows.map((row, i) => {
            prepareRow(row)

            return (
              // The key prop is retrieved by getRowProps()
              // eslint-disable-next-line react/jsx-key
              <tr {...row.getRowProps()} className={cn(styles.tr, row.isExpanded && styles.isExpanded)}>
                {row.cells.map((cell: any, i, { length }) => {
                  const { column } = cell
                  const rowExpandedProps = column.isExpandable && isSmallAndUp && row.getToggleRowExpandedProps()

                  return (
                    // The key prop is retrieved by getCellProps()
                    // eslint-disable-next-line react/jsx-key
                    <td
                      {...cell.getCellProps()}
                      {...rowExpandedProps}
                      className={cn(styles.td, column.className, { [styles.lastTd]: i === length - 1 })}
                    >
                      {cell.render('Cell')}
                    </td>
                  )
                })}

                {/* Expanded content */}
                {(row.isExpanded || !isSmallAndUp) && (
                  <td colSpan={visibleColumns.length} className={cn(styles.td, styles.expanderRow)}>
                    {renderRowSubComponent({ row })}
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </>
  )
}

const Table: FC<TableProps> = ({
  columns,
  query,
  setQueryParams,
  renderRowSubComponent,
  className,
  resultsPerPage = [10, 100, 1000],
  defaultResultsPerPage = resultsPerPage[0],
}) => {
  const isSmallAndUp = useMediaQuery('(min-width: 768px)')
  const { data = { results: [] }, isFetching, isError } = query
  const { total_results: total, limit, page } = data?.meta ?? {}

  const handleLimitChange = useCallback(
    /* @ts-ignore TODO: remove ignore TS validation */
    (value) => setQueryParams((prev: any) => ({ ...prev, limit: value, page: 1 })),
    [setQueryParams],
  )

  return (
    <div className={styles.wrapper}>
      <ResultsPerPage options={resultsPerPage} selectedOption={defaultResultsPerPage} onChange={handleLimitChange} />

      {isFetching && <Loading className={styles.loading} />}

      {data?.results && (
        <TableInner
          columns={columns}
          results={data.results}
          renderRowSubComponent={renderRowSubComponent}
          isFetching={isFetching}
          isError={isError}
          isSmallAndUp={isSmallAndUp}
          className={cn(className, isFetching && styles.isLoading)}
        />
      )}

      {total > limit && (
        <Pagination
          isSmallAndUp={isSmallAndUp}
          total={total}
          limit={limit}
          page={page}
          setQueryParams={setQueryParams}
        />
      )}
    </div>
  )
}

export default Table
